import { z } from "zod";
import type { Grant } from "./permissions";
import type { PlatformCapabilities } from "./platform";
import type { Role } from "./roles";

/**
 * Institution ID: the school's short code printed on staff and student ID cards,
 * e.g. "DPS-RKP". Case-insensitive for the person typing it; stored upper-case.
 */
export const institutionIdSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(1, { error: "Enter your Institution ID", abort: true })
  .regex(/^[A-Z0-9][A-Z0-9-]{2,31}$/, {
    error: "Institution ID has 3 to 32 letters, numbers or hyphens",
  });

/**
 * A school person's User ID: 6 to 8 digits. The first 3 are the school's number (the same for
 * everyone in that school), the rest the person's own number, given out in order: 482001,
 * 482002 … 482999, then 4821000 and on (up to 99,999 people per school). Eduleo assigns them;
 * nobody types one in when creating an account. Several schools can share the first 3 digits,
 * so the Institution ID is still needed at sign-in.
 */
export const schoolUserIdSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/\s+/g, ""))
  .pipe(
    z
      .string()
      .min(1, { error: "Enter your User ID", abort: true })
      .regex(/^\d{6,8}$/, { error: "User ID has 6 to 8 digits, like 482057" }),
  );

/** First 3 digits of every User ID in one school, e.g. "482". */
export const SCHOOL_USER_PREFIX = /^[1-9]\d{2}$/;

/** The User ID for person number `n` (1, 2, …) of the school whose number is `prefix`. */
export function schoolUserId(prefix: string, n: number): string {
  if (!SCHOOL_USER_PREFIX.test(prefix) || !Number.isInteger(n) || n < 1 || n > 99_999) {
    throw new Error("No such school User ID");
  }
  return `${prefix}${n < 1000 ? String(n).padStart(3, "0") : String(n)}`;
}

/**
 * User ID of someone on Eduleo's own team (system admin sign-in), e.g. "platform.admin".
 * Case-insensitive. School people use numeric IDs instead (schoolUserIdSchema).
 */
export const userIdSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, { error: "Enter your User ID", abort: true })
  .max(64, { error: "User ID is too long" })
  .regex(/^[a-z0-9][a-z0-9._@-]*$/, {
    error: "User ID can use letters, numbers, dots, hyphens and @",
  });

/** Password as typed at login. Strength rules apply only when a password is set. */
export const loginPasswordSchema = z
  .string()
  .min(1, { error: "Enter your password", abort: true })
  .max(128, { error: "Password is too long" });

export const PASSWORD_MIN_LENGTH = 10;

/** Rules for a new password. Length matters more than symbols (NIST SP 800-63B). */
export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, {
    error: `Use at least ${PASSWORD_MIN_LENGTH} characters`,
  })
  .max(128, { error: "Password is too long" })
  .refine((value) => !/^(.)\1+$/.test(value), {
    error: "Password can't be one repeated character",
  });

export const loginRequestSchema = z.object({
  institutionId: institutionIdSchema,
  userId: schoolUserIdSchema,
  password: loginPasswordSchema,
});

export type LoginRequest = z.input<typeof loginRequestSchema>;
export type LoginCredentials = z.output<typeof loginRequestSchema>;

export interface SessionUser {
  id: string;
  loginId: string;
  displayName: string;
  roles: Role[];
  /**
   * `logoUpdatedAt` is when the school's logo last changed (null: no logo); the app fetches the
   * picture from /institution/logo and uses this to know when to fetch it again.
   */
  institution: { code: string; name: string; logoUpdatedAt: string | null };
  /**
   * True when someone else set this password (a new account, a reset). Until the person picks
   * their own, the API only lets them change it.
   */
  mustChangePassword: boolean;
  /**
   * A family member whose children's records are still being completed or checked: until the
   * school approves one, only the welcome page works.
   */
  onboarding?: boolean;
}

/** Choosing your own password: the current one (temporary or not) and a new, different one. */
export const passwordChangeSchema = z
  .object({
    currentPassword: loginPasswordSchema,
    newPassword: newPasswordSchema,
  })
  .refine((value) => value.newPassword !== value.currentPassword, {
    error: "Choose a password different from the current one",
    path: ["newPassword"],
    when: (payload) => payload.issues.length === 0,
  });

export type PasswordChange = z.output<typeof passwordChangeSchema>;

/** How long an emailed "set your password" link works. */
export const ACCOUNT_SETUP_LINK_HOURS = 72;

/**
 * The secret of a "set your password" link: the school's Institution ID, a dot, then 43
 * base64url characters (256 random bits). It travels in the link's #fragment, which browsers
 * never send to a server, and then only in request bodies.
 */
export const setupTokenSchema = z
  .string()
  .trim()
  .regex(/^[A-Z0-9][A-Z0-9-]{2,31}\.[A-Za-z0-9_-]{43}$/, {
    error: "This link isn't complete. Open it again from the email.",
  });

export const accountSetupCheckSchema = z.object({ token: setupTokenSchema });

/** Choosing your password from the emailed link. */
export const accountSetupSchema = z
  .object({
    token: setupTokenSchema,
    newPassword: newPasswordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    error: "The two passwords don't match",
    path: ["confirmPassword"],
    when: (payload) => !payload.issues.some((issue) => issue.path?.[0] === "newPassword"),
  });

export type AccountSetup = z.output<typeof accountSetupSchema>;
export type AccountSetupInput = z.input<typeof accountSetupSchema>;

/** Who the link is for, shown before they choose a password. */
export interface AccountSetupInfoDto {
  institution: { code: string; name: string };
  user: { loginId: string; displayName: string };
  expiresAt: string;
}

export interface LoginResponse {
  accessToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
  user: SessionUser;
}

/** What the signed-in person may do, so the UI can hide controls. The API still checks. */
export interface Capabilities {
  /**
   * `privateDetails` / `healthDetails`: may enter religion, caste and income, or medical details,
   * when adding a student (an edit goes by what the student's record shows).
   */
  students: { list: boolean; create: boolean; privateDetails?: boolean; healthDetails?: boolean };
  /** Classes, sections and academic years (School settings). */
  academics: { view: boolean; manage: boolean };
  /** School settings → Admission form: the details, questions and documents asked. */
  admission: { manage: boolean };
  /** Staff accounts (School settings → Staff and roles). */
  people: { view: boolean; manage: boolean; resetPassword: boolean };
  /** The school's roles; `grantPrivileged` allows giving out access permissions. */
  roles: { view: boolean; manage: boolean; grantPrivileged: boolean };
  /** What the person holds, so the role editor offers only what they can give. */
  grants: Grant[];
  /** Platform console permissions; null for everyone outside Eduleo's own team. */
  platform: PlatformCapabilities | null;
}

export interface MeResponse {
  user: SessionUser;
  capabilities: Capabilities;
  /**
   * A family member whose children's records are still with them or the school: only the
   * welcome page (completing the record) works until the school approves one.
   */
  onboarding?: boolean;
}

/**
 * Header the web app sends on cookie-authenticated calls (refresh, logout).
 * A custom header forces a CORS preflight, which blocks cross-site form posts.
 */
export const CLIENT_HEADER = "x-eduleo-client";
export const CLIENT_HEADER_VALUE = "web";
